import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { addTicketConfirmation } from '../services/api';
import { slugifyTicketType } from '../utils/slugify';

function isRepMarker(marker) {
  if (!marker) return false;
  if (marker._layerType === 'fieldRep' || marker._live) return true;
  if (marker._isStack && marker._stackMembers?.[0]?._layerType === 'fieldRep') return true;
  return false;
}

function ticketMembers(marker) {
  if (!marker || isRepMarker(marker)) return [];
  if (marker._isStack && marker._stackMembers) {
    return marker._stackMembers.filter(m => m.recordId);
  }
  return marker.recordId ? [marker] : [];
}

function typeSlug(ticket) {
  return slugifyTicketType(ticket.projectName, ticket.serviceType, ticket.itemName);
}

function typeLabel(ticket) {
  return [ticket.itemName, ticket.serviceType].filter(Boolean).join(' · ') || 'this type';
}

export function hiddenTypeSlugsFor(marker) {
  const seen = new Set();
  const slugs = [];
  for (const ticket of ticketMembers(marker)) {
    const slug = typeSlug(ticket);
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    slugs.push(slug);
  }
  return slugs;
}

export function MarkerActionMenu({
  marker,
  point,
  canEdit,
  onClose,
  onSendMessage,
  onRequestCheckIn,
  onHideTypes,
  onShowOnlyTypes,
  onHideReps,
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState(point || { x: 0, y: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !point) return;
    const rect = el.getBoundingClientRect();
    let x = point.x + 8;
    let y = point.y + 8;
    if (x + rect.width > window.innerWidth - 8) x = Math.max(8, point.x - rect.width - 8);
    if (y + rect.height > window.innerHeight - 8) y = Math.max(8, window.innerHeight - rect.height - 8);
    setPos({ x, y });
  }, [point, marker]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    // The opening click is still unwinding when this mounts. Arm on the
    // next turn so that click cannot immediately dismiss the menu.
    let armed = false;
    const arm = window.setTimeout(() => { armed = true; }, 0);
    const onPointer = (e) => {
      if (!armed || e.button === 2) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    return () => {
      window.clearTimeout(arm);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
    };
  }, [onClose]);

  if (!marker || !point) return null;

  const reps = isRepMarker(marker);
  const tickets = ticketMembers(marker);
  const single = tickets.length === 1 ? tickets[0] : null;
  const slugs = hiddenTypeSlugsFor(marker);
  const title = reps
    ? (marker.name || marker._stackMembers?.[0]?.name || 'Field rep')
    : (single?.siteName || (tickets.length > 1 ? `${tickets.length} tickets` : 'Ticket'));

  const hideLabel = slugs.length > 1
    ? `Hide ${slugs.length} types at this pin`
    : `Hide ${typeLabel(tickets[0] || {})}`;
  const showOnlyLabel = slugs.length > 1
    ? `Show only ${slugs.length} types at this pin`
    : `Show only ${typeLabel(tickets[0] || {})}`;

  const run = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div
      ref={ref}
      className="marker-action-menu"
      style={{ left: pos.x, top: pos.y }}
      role="menu"
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="marker-action-title">{title}</div>
      {!reps && single && (
        <button type="button" role="menuitem" onClick={run(() => {
          window.open(
            `https://siteservicesnow.quickbase.com/nav/app/bjt99j3pb/table/bjwdgf796/action/dr?rid=${single.recordId}`,
            '_blank',
            'noopener'
          );
          onClose();
        })}>
          Open in QuickBase
        </button>
      )}
      {!reps && tickets.length > 0 && (
        <button type="button" role="menuitem" onClick={run(() => { onSendMessage(marker); onClose(); })}>
          Send message
        </button>
      )}
      {!reps && single && canEdit && (
        <button type="button" role="menuitem" onClick={run(() => { onRequestCheckIn(single); onClose(); })}>
          Request check-in
        </button>
      )}
      {!reps && single && (
        <button type="button" role="menuitem" onClick={run(async () => {
          try { await navigator.clipboard.writeText(String(single.recordId)); } catch { /* ignore */ }
          onClose();
        })}>
          Copy ticket #
        </button>
      )}
      {!reps && slugs.length > 0 && (
        <button type="button" role="menuitem" className="marker-action-hide" onClick={run(() => { onHideTypes(slugs); onClose(); })}>
          {hideLabel}
        </button>
      )}
      {!reps && slugs.length > 0 && onShowOnlyTypes && (
        <button type="button" role="menuitem" className="marker-action-hide" onClick={run(() => { onShowOnlyTypes(slugs); onClose(); })}>
          {showOnlyLabel}
        </button>
      )}
      {reps && (
        <button type="button" role="menuitem" className="marker-action-hide" onClick={run(() => { onHideReps(marker); onClose(); })}>
          {marker._live ? 'Hide live locations' : 'Hide field reps'}
        </button>
      )}
    </div>
  );
}

export function CheckInRequestDialog({ ticket, onClose, onSaved }) {
  const [notes, setNotes] = useState('Check-in requested.');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const submit = async (e) => {
    e.preventDefault();
    const text = notes.trim();
    if (!text) {
      setError('Add a short note.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await addTicketConfirmation(ticket.recordId, {
        type: 'Day Of',
        notes: text,
        confirmedTime: new Date().toISOString(),
      });
      onSaved(result.ticket);
    } catch (err) {
      setError(err.message || 'Failed to save check-in request');
      setSaving(false);
    }
  };

  return (
    <div className="checkin-backdrop" onMouseDown={onClose}>
      <form
        className="checkin-dialog"
        onMouseDown={e => e.stopPropagation()}
        onSubmit={submit}
      >
        <h3>Request check-in</h3>
        <p className="checkin-sub">
          {ticket.siteName || `Ticket #${ticket.recordId}`} · logs a Day Of confirmation
        </p>
        <textarea
          value={notes}
          rows={3}
          onChange={e => setNotes(e.target.value)}
          disabled={saving}
          autoFocus
        />
        {error && <div className="checkin-error">{error}</div>}
        <div className="checkin-actions">
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="checkin-save" disabled={saving}>
            {saving ? 'Saving…' : 'Save request'}
          </button>
        </div>
      </form>
    </div>
  );
}
